import { useEffect, useRef, useState } from 'react';

import { FileTypeName, PermissionType } from 'api/types/entity';
import { DocumentsTable } from 'components';
import { useField } from 'libs/form/fields/useField';
import { useFormContext } from 'libs/form/useFormContext';
import { useTranslation } from 'libs/i18n';
import { usePermissions } from 'libs/permissions';
import { IconButton, Stack, Typography } from 'libs/ui';
import { Icon } from 'libs/ui/Icon';
import { TypographyVariant } from 'libs/ui/theme/typography';
import { FileExtension, LocalFileEntity } from 'types/file';
import { PropsWithTestAttribute } from 'types/testAttribute';

import { useDocumentField } from './hooks';

export interface DocumentsTableFeatureProps extends PropsWithTestAttribute {
  readOnly?: boolean;
  title?: string;
  titleVariant?: TypographyVariant;
  maxDocumentCount?: number;
  name: string;
  fileType?: FileTypeName;
  fileExtensions?: FileExtension[];
}

type FileType = LocalFileEntity & { loading?: boolean } & { id: string };

export const DocumentsTableFeature = ({
  readOnly,
  title,
  maxDocumentCount,
  name,
  datax,
  fileType,
  fileExtensions,
  titleVariant = 'bodyBold',
}: DocumentsTableFeatureProps) => {
  const { t } = useTranslation();

  const { checkPermission } = usePermissions();

  const [isError, setIsError] = useState(false);

  const canDeleteAnyDocument = checkPermission(
    PermissionType.Document_DeleteAnyOne
  );

  const { field, inputProps, meta, helpers } = useField<FileType[]>(name);

  const { readOnly: contextReadOnly } = useFormContext();

  const { open, getInputProps, onClickDelete } = useDocumentField({
    name,
    maxDocumentCount,
    fileType,
    fileExtensions,
  });

  title = title ?? t('components.documentsTableFeature.title');

  const isFirstRender = useRef(true);

  const files = field.value ?? [];

  const editable = !(readOnly ?? contextReadOnly);

  const shownHead = !!title || editable;
  const disabledUpload =
    files.length > 0 && files.length >= (maxDocumentCount ?? Infinity);

  // Clear error state on files change
  useEffect(() => {
    // Skip on first render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setIsError(false);
    helpers.setError(undefined);
  }, [files.length, helpers]);

  useEffect(() => {
    if (meta.isError) {
      setIsError(true);
    } else {
      setIsError(false);
    }
  }, [meta.isError]);

  return (
    <Stack datax={datax} spacing={12}>
      <input {...getInputProps()} aria-invalid={inputProps.error} name={name} />

      {shownHead && (
        <Stack alignItems="center" direction="row" spacing={16}>
          <Typography variant={titleVariant}>{title}</Typography>
          {editable && (
            <IconButton
              disabled={disabledUpload}
              icon={<Icon.Plus size={16} />}
              mode="outlined"
              sizeVariant="xs"
              onClick={open}
            />
          )}
        </Stack>
      )}
      <DocumentsTable
        canDeleteAnyDocument={canDeleteAnyDocument}
        editable={editable}
        files={files}
        isError={isError}
        noRowsLabel={isError ? t('validation.files') : undefined}
        onClickDelete={onClickDelete}
      />
    </Stack>
  );
};
